/* eslint-disable no-underscore-dangle */
import { DateSelectArg, DatesSetArg, EventClickArg, EventSourceInput } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import { ResourceApi, ResourceSourceInput } from '@fullcalendar/resource';
import { Box, Button, PopoverPosition } from '@mui/material';
import {
  useOperatorsScheduleVehiclesQuery,
  useOperatorsVehiclesAllQuery,
} from 'api/queries/operators';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { ScheduleQueryParams } from 'api/types';
import VehicleCreateModalForm from 'components/forms/FleetFormFactory/VehicleCreateModalForm';
import dayjs from 'dayjs';
import { DEFAULT_MAX_RANGE_PASSENGER_COUNT, ScheduleItemType, UserPermission } from 'remote/global';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { AddEventTypeEnum, SchedulerTypeEnum } from 'utils/data/data-types';
import { ROUTES } from 'utils/data/routes';
import { formatDateWithTz, hasWritePermission } from 'utils/helpers';
import { SchedulerOverlay } from 'utils/component-utils';
import AddEventDropdown, { AddEventDropdownList } from 'components/scheduler/AddEventDropdown';
import SchedulerPageHeader from 'components/scheduler/SchedulerPageHeader';
import WrappedOrderDetailsModal from 'components/modals/OrderModal/WrappedOrderDetailsModal';
import CustomFullCalendar from 'components/scheduler/CustomFullCalendar';
import VehicleRepairModal from 'components/modals/VehicleRepairModal';
import VehicleOfflineModal from 'components/modals/VehicleOfflineModal';
import CreateRideModal from 'components/modals/CreateRideModal';
import CreateCharterRideModal from 'components/modals/CreateCharterRideModal';

enum VehicleSeatsOrderEnum {
  'SEATS_6_9' = '6',
  'SEATS_10_21' = '5',
  'SEATS_22_30' = '4',
  'SEATS_31_35' = '3',
  'SEATS_36_51' = '2',
  'SEATS_52_69' = '1',
  'SEATS_70_95' = '0',
}

type SelectedRange = {
  position?: PopoverPosition;
  data: {
    start?: Date;
    end?: Date;
  };
};

function VehicleSchedulerView() {
  const [isAddRideModalOpen, setIsAddRideModalOpen] = useState(false);
  const [isRegularTripModalOpen, setIsRegularTripModalOpen] = useState(false);
  const [isBusModalOpen, setIsBusModalOpen] = useState(false);
  const [isRepairModalOpen, setIsRepairModalOpen] = useState(false);
  const [isOfflineModalOpen, setIsOfflineModalOpen] = useState(false);
  const [selectedDateString, setSelectedDateString] = useState('');
  const [selectedRange, setSelectedRange] = useState<SelectedRange | undefined>();
  const [selectedCellInfo, setSelectedCellInfo] = useState<DateSelectArg | undefined>();
  const [isRepairEditMode, setIsRepairEditMode] = useState(false);
  const [isOfflineEditMode, setIsOfflineEditMode] = useState(false);
  const [isEventDropdownListOpen, setIsEventDropdownListOpen] = useState(false);
  const [isOrderDetailsModalOpen, setIsOrderDetailsModalOpen] = useState(false);
  const [isCreateOfferModalOpen, setIsCreateOfferModalOpen] = useState(false);
  const { t } = useTranslation();
  const calendarRef = useRef<FullCalendar>(null);
  const [queryParams, setQueryParams] = useState<ScheduleQueryParams | undefined>();
  const {
    data: schedule,
    isLoading,
    refetch: refetchScheduleVehicles,
  } = useOperatorsScheduleVehiclesQuery(queryParams);
  const [bookingItem, setBookingItem] = useState<
    { bookingNumber: string; rideId: number } | undefined
  >();

  const selectedLicensePlate = selectedCellInfo?.resource?.extendedProps?.licensePlate ?? '';
  // Make sure to fetch the vehicles if they are not already fetched when a licensePlate is available (occurs when selecting a range in the scheduler)
  const isAllVehiclesQueryEnabled = selectedLicensePlate.length > 0 && isAddRideModalOpen;
  const { data: allVehicles, isFetched: isAllVehiclesFetched } =
    useOperatorsVehiclesAllQuery(isAllVehiclesQueryEnabled);
  const selectedVehicle = allVehicles?.find((v) => v.licensePlate === selectedLicensePlate);

  // Never ever just access state.data because we rely on state.data.hoveredEvent to not rerender this component, otherwise the viewMore popover just dissapears
  const { data: userInfo } = useUsersCurrentAccessesQuery();

  const { permissions = {} } = userInfo || {};

  const eventSource = useMemo(() => {
    const getVehicle = (ownerId: number) => {
      const foundVehicle = schedule?.vehicles.find((vehicle) => vehicle.id === ownerId);
      return foundVehicle;
    };

    const getSeatsRange = (ownerId: number) => {
      const vehicle = getVehicle(ownerId);
      return vehicle?.seatsRange;
    };

    const getSeatsOrder = (ownerId: number) => {
      const vehicle = getVehicle(ownerId);
      return VehicleSeatsOrderEnum[vehicle?.seatsRange as keyof typeof VehicleSeatsOrderEnum];
    };

    return schedule?.scheduleItems?.map((event) => {
      const vehicle = getVehicle(event.ownerId);
      return {
        ...vehicle,
        ...event,
        resourceId: event.ownerId,
        title: event.bookingNumber || '',
        start: event.startTime,
        end: event.endTime,
        orderType: event.orderType,
        rideNumber: event.rideNumber,
        rideNumberTranslated: event.rideNumber
          ? t('terms.attributes.ride.rideNo', { rideNo: event.rideNumber })
          : '',
        itemTypeTranslated: t(`ScheduleItemType.${event.itemType}`),
        seatsRange: getSeatsRange(event.ownerId),
        seatsRangeOrder: getSeatsOrder(event.ownerId),
      };
    });
  }, [schedule?.scheduleItems, schedule?.vehicles, t]);

  const elementSource = useMemo(() => {
    if (schedule?.vehicles?.length) {
      return schedule.vehicles.map((vehicle) => {
        return {
          ...vehicle,
          title: vehicle.licensePlate,
          subtitle: t('SchedulerView.attributes.numOfSeats', { seats: vehicle.seats }),
          tooltip: vehicle.make,
          url: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.FLEET, vehicle.id].join('/'),
          seatsRangeOrder:
            VehicleSeatsOrderEnum[vehicle?.seatsRange as keyof typeof VehicleSeatsOrderEnum],
          ownerId: vehicle.id,
        };
      });
    }
    return [];
  }, [schedule?.vehicles, t]);

  const handleSetSelectedCellInfo = (args: EventClickArg) => {
    setSelectedCellInfo({
      ...args,
      ...args.event,
      start: dayjs(args?.event?._instance?.range.start).utc() as unknown as Date,
      end: dayjs(args?.event?._instance?.range.end).utc() as unknown as Date,
      resource: {
        ...(args as unknown as DateSelectArg).resource,
        id: args.event.id,
        extendedProps: args.event._def.extendedProps,
      } as ResourceApi,
    } as unknown as DateSelectArg);
  };

  const handleEventClick = async (args: EventClickArg) => {
    const { event } = args ?? {};
    const { rideId, itemType, bookingNumber } = event.extendedProps;

    switch (itemType) {
      case ScheduleItemType.RIDE:
        if (
          !bookingNumber ||
          !rideId ||
          [UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions?.ORDER)
        )
          return;
        try {
          setBookingItem({ bookingNumber, rideId });
          setIsOrderDetailsModalOpen(true);
        } catch (err) {
          toast.error(t('Toast.failure'));
        }
        break;
      case ScheduleItemType.REPAIR:
        if (
          [UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
            permissions?.VEHICLE_SCHEDULE
          ) ||
          [UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
            permissions?.DRIVER_SCHEDULE
          )
        )
          return;
        setIsRepairModalOpen(true);
        setIsRepairEditMode(true);
        handleSetSelectedCellInfo(args);
        break;
      case ScheduleItemType.OFFLINE:
        if (
          [UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
            permissions?.VEHICLE_SCHEDULE
          ) ||
          [UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
            permissions?.DRIVER_SCHEDULE
          )
        )
          return;
        setIsOfflineModalOpen(true);
        setIsOfflineEditMode(true);
        handleSetSelectedCellInfo(args);
        break;
      default:
    }
  };

  const handleDateChange = async (args: DatesSetArg) => {
    const { startStr, endStr, view } = args;

    // We create this string match to validate if the request should be made
    // handleDateChange might be called multiple times with the same values
    // and this will lead to an infinite loop
    const part = `${startStr}${endStr}${view}`;
    if (part === selectedDateString) return;

    setQueryParams({
      start: dayjs(startStr).format('YYYY-MM-DD'),
      end: dayjs(endStr).format('YYYY-MM-DD'),
    });

    setSelectedDateString(part);
  };

  const handleSelectHeaderEvent = (option: AddEventTypeEnum) => {
    switch (option) {
      case AddEventTypeEnum.OFFLINE:
        setIsOfflineModalOpen(true);
        break;
      case AddEventTypeEnum.RIDE:
        setIsAddRideModalOpen(true);
        break;
      case AddEventTypeEnum.OFFER:
        setIsCreateOfferModalOpen(true);
        break;
      case AddEventTypeEnum.REGULAR_TRIP:
        setIsRegularTripModalOpen(true);
        break;
      case AddEventTypeEnum.REPAIR:
        setIsRepairModalOpen(true);
        break;
      default:
    }
  };

  const handleClearRepairModalState = () => {
    setIsRepairEditMode(false);
    setSelectedRange(undefined);
    setSelectedCellInfo(undefined);
    setSelectedDateString('');
  };

  const handleClearOfflineModalState = () => {
    setIsOfflineModalOpen(false);
    setIsOfflineEditMode(false);
    setSelectedRange(undefined);
    setSelectedCellInfo(undefined);
    setSelectedDateString('');
  };

  const handleAddRideModalClose = () => {
    setIsAddRideModalOpen(false);
    setSelectedRange(undefined);
    setSelectedCellInfo(undefined);
  };

  const handleCreateOfferModalClose = () => {
    setIsCreateOfferModalOpen(false);
    setSelectedRange(undefined);
    setSelectedCellInfo(undefined);
  };

  const handleRegularTripModalSubmit = () => {
    setIsRegularTripModalOpen(false);
    // Refetches the schedule based on current query params
    refetchScheduleVehicles();
  };

  const showAddFirstFleetButton =
    !schedule?.vehicles?.length && !isLoading && hasWritePermission(permissions?.VEHICLE_DETAILS);

  const handleSelectRange = (range: DateSelectArg) => {
    if (range.jsEvent != null) {
      setSelectedRange({
        position: {
          left: range.jsEvent.clientX,
          top: range.jsEvent.clientY,
        },
        data: {
          start: range.start,
          end: range.end,
        },
      });
    }
    setSelectedCellInfo(range);
    setIsEventDropdownListOpen(true);
  };

  const getAddEventOptions = () => {
    const options = [];
    if (![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions.ORDER)) {
      options.push(AddEventTypeEnum.RIDE);
    }
    if (
      ![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions.OPERATOR_OFFER)
    ) {
      options.push(AddEventTypeEnum.OFFER);
    }
    if (![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions.RIDE)) {
      options.push(AddEventTypeEnum.OFFLINE);
    }
    if (
      ![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
        permissions.DRIVER_SCHEDULE
      ) &&
      ![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions.VEHICLE_SCHEDULE)
    )
      options.push(AddEventTypeEnum.REPAIR);

    return options;
  };

  const handleCloseEventDropdown = () => {
    setSelectedRange(undefined);
    setSelectedCellInfo(undefined);
    setSelectedDateString('');

    setIsEventDropdownListOpen(false);
    setIsRepairModalOpen(false);
    setIsAddRideModalOpen(false);
    setIsRepairEditMode(false);

    setBookingItem(undefined);
  };

  const getIsAddRideModalOpen = () => {
    if (!isAddRideModalOpen) return false;
    // Makes sure that vehicles are fetched before showing the modal when a licensePlate is available
    if (selectedLicensePlate.length > 0 && !isAllVehiclesFetched) return false;
    return true;
  };
  const isAddRideModalRendered = getIsAddRideModalOpen();
  const getIsCreateOfferModalOpen = () => {
    if (!isCreateOfferModalOpen) return false;
    // Makes sure that vehicles are fetched before showing the modal when a licensePlate is available
    if (selectedLicensePlate.length > 0 && !isAllVehiclesFetched) return false;
    return true;
  };
  const isCreateOfferModalRendered = getIsCreateOfferModalOpen();

  const handleClearOrderDetailsModalState = () => {
    setIsOrderDetailsModalOpen(false);
    setBookingItem(undefined);
  };

  const rideDefaultValues = [
    {
      licensePlate: selectedLicensePlate,
      startTime: formatDateWithTz(selectedCellInfo?.start)!,
      returnTime: formatDateWithTz(selectedCellInfo?.end)!,
      maxPassengerCount: selectedVehicle?.seats ?? DEFAULT_MAX_RANGE_PASSENGER_COUNT,
    },
  ];

  return (
    <>
      {bookingItem && (
        <WrappedOrderDetailsModal
          open={isOrderDetailsModalOpen}
          setOpen={setIsOrderDetailsModalOpen}
          onClose={handleClearOrderDetailsModalState}
          bookingNumber={bookingItem.bookingNumber}
          rideId={bookingItem.rideId}
        />
      )}
      <CreateRideModal
        open={isAddRideModalRendered}
        setOpen={setIsAddRideModalOpen}
        onClose={handleAddRideModalClose}
        defaultValues={rideDefaultValues as any}
      />
      <CreateRideModal
        isCreateOfferVariant
        open={isCreateOfferModalRendered}
        setOpen={setIsCreateOfferModalOpen}
        onClose={handleCreateOfferModalClose}
        defaultValues={rideDefaultValues as any}
      />
      <CreateCharterRideModal
        open={isRegularTripModalOpen}
        setOpen={setIsRegularTripModalOpen}
        onClose={() => setIsRegularTripModalOpen(false)}
        onSubmit={handleRegularTripModalSubmit}
      />
      <VehicleRepairModal
        open={isRepairModalOpen}
        setOpen={setIsRepairModalOpen}
        onClose={handleClearRepairModalState}
        isEditMode={isRepairEditMode}
        defaultValues={{
          startTime: formatDateWithTz(selectedCellInfo?.start),
          endTime: formatDateWithTz(selectedCellInfo?.end),
          ownerId: selectedCellInfo?.resource?.extendedProps.ownerId ?? '',
          resourceId: selectedCellInfo ? Number(selectedCellInfo?.resource?.id) : undefined,
        }}
      />
      <VehicleOfflineModal
        open={isOfflineModalOpen}
        setOpen={setIsOfflineModalOpen}
        onClose={handleClearOfflineModalState}
        isEditMode={isOfflineEditMode}
        defaultValues={{
          startTime: formatDateWithTz(selectedCellInfo?.start),
          endTime: formatDateWithTz(selectedCellInfo?.end),
          ownerId: selectedCellInfo?.resource?.extendedProps.ownerId ?? '',
          resourceId: selectedCellInfo ? Number(selectedCellInfo?.resource?.id) : undefined,
        }}
      />
      <VehicleCreateModalForm isModalOpen={isBusModalOpen} setIsModalOpen={setIsBusModalOpen} />
      <AddEventDropdownList
        schedulerType={SchedulerTypeEnum.BUS_SCHEDULER}
        addEventOptions={getAddEventOptions()}
        open={isEventDropdownListOpen}
        handleClose={(exitWithoutSelection) => {
          // if exitWithoutSelection reset all selection state
          if (exitWithoutSelection) handleCloseEventDropdown();
          else setIsEventDropdownListOpen(false);
        }}
        handleEventClick={handleSelectHeaderEvent}
        anchorReference='anchorPosition'
        anchorPosition={selectedRange?.position}
      />
      {showAddFirstFleetButton && (
        <SchedulerOverlay>
          <Button
            data-testid='add-first-fleet-button'
            sx={{
              marginTop: '100px',
            }}
            onClick={() => {
              setIsBusModalOpen(true);
            }}
          >
            {t('SchedulerView.actions.addFirstVehicle')}
          </Button>
        </SchedulerOverlay>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          position: 'relative',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            mb: 1,
          }}
        >
          <AddEventDropdown
            schedulerType={SchedulerTypeEnum.BUS_SCHEDULER}
            handleEventClick={handleSelectHeaderEvent}
          />
          <SchedulerPageHeader calendarRef={calendarRef} />
        </Box>
        <CustomFullCalendar
          resources={elementSource as ResourceSourceInput}
          events={eventSource as EventSourceInput}
          schedulerType={SchedulerTypeEnum.BUS_SCHEDULER}
          calendarRef={calendarRef}
          handleEventClick={handleEventClick}
          handleDateChange={handleDateChange}
          handleSelectRange={handleSelectRange}
          calendarProps={{
            initialView: 'resourceTimelineTwoWeek',
            resourceAreaHeaderContent: t('SchedulerView.tabs.vehicles'),
            resourceGroupField: 'seatsRangeOrder',
            resourceOrder: '-seatsRangeOrder',
          }}
        />
      </Box>
    </>
  );
}

export default VehicleSchedulerView;
